import { useMemo, useState } from "react";
import { Upload, Table, X, CheckCircle, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal for pasting a block of product rows from Excel/Google Sheets/CSV.
 *
 * - Auto-detects the delimiter (TAB > comma > semicolon).
 * - Auto-detects the header row (first line is treated as headers if any cell
 *   matches a known column alias; otherwise we assume a fixed column order
 *   `nombre, categoria, precio_venta, costo, cantidad`).
 * - Maps headers (case + accent insensitive) onto Product fields.
 * - Renders a live preview so the seller can verify before committing.
 * - Calls POST /api/products/bulk-create on confirm and toasts a summary.
 */
const COLUMN_ALIASES = {
  nombre: ["nombre", "producto", "articulo", "name", "title"],
  categoria: ["categoria", "categoría", "category", "rubro"],
  descripcion: ["descripcion", "descripción", "description", "detalle"],
  costo: ["costo", "cost", "compra", "precio compra"],
  precio_venta: ["precio_venta", "precio", "venta", "price", "precio venta"],
  cantidad: ["cantidad", "stock", "qty", "existencia", "unidades"],
  condicion: ["condicion", "condición", "estado", "condition"],
  para_que_sirve: ["para que sirve", "uso", "usage", "para_que_sirve"],
  especificaciones: ["especificaciones", "specs", "ficha", "ficha tecnica"],
};

const NUMERIC_KEYS = new Set(["costo", "precio_venta", "cantidad"]);
const CONDICION_VALID = new Set(["nuevo", "usado", "oportunidad"]);
const FIXED_COLUMN_ORDER = ["nombre", "categoria", "precio_venta", "costo", "cantidad"];

function normalizeKey(s) {
  return (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function detectDelimiter(text) {
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const tabs = (firstLine.match(/\t/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  const semis = (firstLine.match(/;/g) || []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return "\t";
  if (semis > commas && semis > 0) return ";";
  return ",";
}

function splitCsvLine(line, delim) {
  // Minimal CSV-ish parser that honours quoted strings (Excel pastes them).
  const out = [];
  let i = 0;
  let cell = "";
  let quoted = false;
  while (i < line.length) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 2;
        continue;
      }
      if (ch === '"') {
        quoted = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
    } else {
      if (ch === '"' && cell === "") {
        quoted = true;
        i += 1;
        continue;
      }
      if (ch === delim) {
        out.push(cell);
        cell = "";
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
    }
  }
  out.push(cell);
  return out.map((c) => c.trim());
}

function buildHeaderMap(headerCells) {
  const map = {};
  headerCells.forEach((cell, idx) => {
    const norm = normalizeKey(cell);
    for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
      if (aliases.some((a) => normalizeKey(a) === norm)) {
        map[idx] = field;
        break;
      }
    }
  });
  return map;
}

function looksLikeHeader(cells) {
  // If any cell matches an alias we treat the first row as headers.
  return cells.some((cell) => {
    const norm = normalizeKey(cell);
    return Object.values(COLUMN_ALIASES).some((arr) =>
      arr.some((a) => normalizeKey(a) === norm),
    );
  });
}

function coerceCell(field, raw) {
  const value = (raw ?? "").toString().trim();
  if (NUMERIC_KEYS.has(field)) {
    if (!value) return field === "cantidad" ? 1 : 0;
    const clean = value.replace(/[^\d.,-]/g, "").replace(/,/g, ".");
    const n = Number(clean);
    return Number.isFinite(n) ? n : 0;
  }
  if (field === "condicion") {
    const v = normalizeKey(value);
    return CONDICION_VALID.has(v) ? v : "usado";
  }
  return value;
}

export function parseClipboard(text) {
  const trimmed = text.replace(/^\s+|\s+$/g, "");
  if (!trimmed) return { rows: [], headers: [], skipped: 0 };
  const delim = detectDelimiter(trimmed);
  const lines = trimmed
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { rows: [], headers: [], skipped: 0 };

  const firstCells = splitCsvLine(lines[0], delim);
  const isHeader = looksLikeHeader(firstCells);
  const headerMap = isHeader ? buildHeaderMap(firstCells) : null;
  const dataLines = isHeader ? lines.slice(1) : lines;

  const rows = [];
  let skipped = 0;
  for (const line of dataLines) {
    const cells = splitCsvLine(line, delim);
    if (cells.every((c) => !c)) {
      skipped += 1;
      continue;
    }
    const row = {};
    if (headerMap) {
      cells.forEach((cell, idx) => {
        const field = headerMap[idx];
        if (field) row[field] = coerceCell(field, cell);
      });
    } else {
      FIXED_COLUMN_ORDER.forEach((field, idx) => {
        if (idx < cells.length) row[field] = coerceCell(field, cells[idx]);
      });
    }
    if (!row.nombre) {
      skipped += 1;
      continue;
    }
    if (!row.categoria) row.categoria = "Otro";
    if (!row.condicion) row.condicion = "usado";
    rows.push(row);
  }
  const headers = isHeader
    ? firstCells.map((c, idx) => ({ raw: c, mapped: headerMap[idx] || null }))
    : FIXED_COLUMN_ORDER.map((f) => ({ raw: f, mapped: f }));
  return { rows, headers, skipped, delim, autoHeader: isHeader };
}

export default function PasteImportModal({ open, onClose, onImported }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const parsed = useMemo(() => parseClipboard(text), [text]);
  const rows = parsed.rows;

  const submit = async () => {
    if (!rows.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/products/bulk-create", { items: rows });
      const ok = data?.ok || 0;
      const fail = data?.fail || 0;
      if (ok) toast.success(`${ok} producto(s) importado(s)`);
      if (fail) toast.error(`${fail} fila(s) con error`);
      onImported?.(data);
      if (!fail) {
        setText("");
        onClose?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="paste-import-modal"
    >
      <div className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl border border-zinc-200">
        <header className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Table size={20} weight="duotone" className="text-klein" />
            <div>
              <div className="text-base font-bold h-display tracking-tight">
                Importar productos pegando desde Excel
              </div>
              <p className="text-xs text-muted2">
                Pega filas con tabuladores (Excel/Sheets) o comas (CSV). Detecta encabezados automáticamente.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-100"
            data-testid="paste-import-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="px-5 pt-4 pb-2">
          <label className="text-[11px] uppercase tracking-wide text-muted2 font-semibold">
            Pega aquí tus filas
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={`Ejemplo (con encabezados):\nnombre\tcategoria\tprecio_venta\tcosto\tcantidad\nMartillo carpintero\tHerramientas\t180\t90\t5\n\nO sin encabezados — usa el orden: nombre, categoria, precio_venta, costo, cantidad`}
            className="input-flat font-mono text-xs w-full mt-1"
            data-testid="paste-import-textarea"
          />
        </div>

        <div className="flex-1 overflow-auto px-5 pb-2">
          {rows.length > 0 ? (
            <>
              <div className="flex items-center justify-between text-xs text-muted2 mb-2">
                <span>
                  <strong>{rows.length}</strong> fila(s) detectada(s)
                  {parsed.skipped > 0 && <> · {parsed.skipped} omitida(s)</>}
                  {parsed.autoHeader && <> · encabezados detectados</>}
                </span>
                <span>
                  Delim: <code>{parsed.delim === "\t" ? "tab" : parsed.delim}</code>
                </span>
              </div>
              <div className="border border-zinc-200 overflow-auto max-h-[280px]" data-testid="paste-import-preview">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-2 w-8">#</th>
                      <th className="text-left px-2 py-2">Nombre</th>
                      <th className="text-left px-2 py-2">Categoría</th>
                      <th className="text-right px-2 py-2">Precio</th>
                      <th className="text-right px-2 py-2">Costo</th>
                      <th className="text-right px-2 py-2">Cant.</th>
                      <th className="text-left px-2 py-2">Condición</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => (
                      <tr key={`${r.nombre || "row"}-${i}`} className="border-t border-zinc-100">
                        <td className="px-2 py-1 text-muted2 tabular-nums">{i + 1}</td>
                        <td className="px-2 py-1 font-medium">{r.nombre}</td>
                        <td className="px-2 py-1">{r.categoria}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {Number(r.precio_venta || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {Number(r.costo || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{r.cantidad || 1}</td>
                        <td className="px-2 py-1">{r.condicion}</td>
                      </tr>
                    ))}
                    {rows.length > 50 && (
                      <tr>
                        <td colSpan={7} className="px-2 py-2 text-center text-muted2">
                          … {rows.length - 50} fila(s) más
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : text.trim() ? (
            <div className="border border-zinc-200 bg-zinc-50 p-6 text-center text-sm text-muted2 flex items-center justify-center gap-2" data-testid="paste-import-empty">
              <Warning size={16} className="text-[#FF2A00]" weight="fill" />
              No se detectaron filas. Verifica que cada producto esté en una línea y separa columnas con TAB o coma.
            </div>
          ) : (
            <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-muted2">
              <Upload size={28} className="mx-auto mb-2 opacity-50" />
              Pega filas para ver una vista previa
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            disabled={busy}
            data-testid="paste-import-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !rows.length}
            className="btn-klein inline-flex items-center gap-2"
            data-testid="paste-import-submit"
          >
            <CheckCircle size={14} weight="fill" />
            {busy ? "Importando…" : `Importar ${rows.length || 0}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
